import { Link, Navigate } from '@tanstack/react-router';
import type { Threshold, ThresholdList } from '@painpal/contracts';
import { useStravaConnect } from '../../api/mutations';
import { useStravaStatus, useThresholds } from '../../api/queries';
import { Headline } from '../../components/Headline';
import { PoweredByStrava } from '../../components/PoweredByStrava';
import { StravaConnectButton } from '../../components/StravaConnectButton';
import { StravaReturnError } from '../../components/StravaReturnError';
import { errorMessage } from '../../lib/api';
import { formatDayMonth } from '../../lib/format';
import { sortAscending } from '../settings/thresholdModel';
import { OnboardingFrame } from './OnboardingFrame';
import styles from './onboarding.module.css';

const BULLETS = ['NEW RIDES APPEAR WITHIN MINUTES', 'FULL HISTORY · ~900 RIDES A DAY', 'DISCONNECT ANY TIME IN SETTINGS'];

export function latestFtp(list: ThresholdList | undefined): Threshold | null {
  return sortAscending(list?.items ?? []).at(-1) ?? null;
}

export function ridesEyebrow(ftp: Threshold | null): string {
  return ftp === null ? 'STEP 02 / 02' : `STEP 02 / 02 · FTP SAVED: ${ftp.value} W FROM ${formatDayMonth(ftp.effectiveFrom)}`;
}

// One element for every render: Navigate navigates again whenever its props object changes, and a fresh element has fresh props.
const GO_TO_FITNESS = <Navigate to="/" replace />;

interface StravaOptionProps { connected: boolean; busy: boolean; error: Error | null; onConnect: () => void }

function StravaOption({ connected, busy, error, onConnect }: StravaOptionProps) {
  return (
    <section className={styles.stravaCard} aria-labelledby="sync-title">
      <span className={styles.tag}>RECOMMENDED · AUTOMATIC</span>
      <h2 id="sync-title" className={styles.cardTitle}>SYNC FROM STRAVA</h2>
      <p className={styles.cardBody}>Connect once and every new ride lands in PainPal by itself. We&apos;ll also pull in your history, newest first.</p>
      <ul className={styles.bullets}>{BULLETS.map((b) => <li key={b}>{b}</li>)}</ul>
      {connected ? (
        <>
          <p className={styles.connected} role="status">STRAVA CONNECTED · TAKING YOU TO FITNESS</p>
          {GO_TO_FITNESS}
        </>
      ) : (
        <div className={styles.actionBar} data-part="action-bar">
          <StravaConnectButton onClick={onConnect} busy={busy} />
        </div>
      )}
      {error !== null && <p role="alert" className="alert-hot">{errorMessage(error)}</p>}
      <PoweredByStrava />
    </section>
  );
}

export function RidesStepPage({ stravaError }: { stravaError?: string }) {
  const thresholds = useThresholds();
  const strava = useStravaStatus();
  const connect = useStravaConnect();
  const connected = strava.data?.connected === true;
  return (
    <OnboardingFrame step={2} skip={<Link to="/" className={styles.skip}>I&apos;LL DO THIS LATER →</Link>}>
      <div className={styles.ridesBody}>
        <div className={styles.ridesHead}>
          <p className={styles.eyebrow}>{ridesEyebrow(latestFtp(thresholds.data))}</p>
          <Headline first="BRING YOUR" second="RIDES." size="onboarding" />
        </div>
        <StravaReturnError error={stravaError} path="/onboarding/rides" />
        <StravaOption connected={connected} busy={connect.isPending} error={connect.error} onConnect={() => connect.mutate({ returnTo: '/onboarding/rides' })} />
        <Link to="/activities" search={{ upload: true }} className={styles.footnote}>
          HAVE .FIT FILES INSTEAD? UPLOAD THEM ANY TIME FROM ACTIVITIES → + ADD ACTIVITY ↗
        </Link>
      </div>
    </OnboardingFrame>
  );
}
